import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import { formatarDataHora } from "@/utils/FormatarEntradas";

dayjs.extend(utc);
dayjs.extend(timezone);

export function useValidacaoAtividade() {
  /**
   * Retorna a estrutura padrão inicial de uma atividade.
   */
  const criarAtividadePadrao = (acaoId = "") => ({
    nome: "",
    descricao: "",
    tipo_id: "",
    subtipo_id: "",
    modalidadeExecucao: "",
    modalidadeInscricao: "",
    local: "",
    acaoExtensaoId: acaoId,
    dataInicioInscricao: "",
    dataFimInscricao: "",
    criterioInscricao: null,
    numeroDeVagasOfertadas: null,
    linkFormulario: "",
    objetivos: [],
    encontros: [],
    criarNoticia: false,
    palavrasChave: [],
    possuiEncontros: true,
    dataInicioRealizacao: "",
    dataFimRealizacao: "",
    imagem: {
      mimeType: "",
      base64Data: "",
      descricaoImagem: "",
    },
  });

  /**
   * Valida se a atividade possui ao menos um objetivo específico (ou objetivo geral marcado)
   * e ao menos uma palavra-chave.
   */
  const validarObjetivosEPalavrasChave = (
    objetivos = [],
    usarObjetivoGeral = false,
    palavrasChave = []
  ) => {
    const temObjetivos =
      (Array.isArray(objetivos) && objetivos.length > 0) ||
      Boolean(usarObjetivoGeral);
    const temPalavrasChave =
      Array.isArray(palavrasChave) && palavrasChave.length > 0;

    if (!temObjetivos && !temPalavrasChave) {
      return {
        valido: false,
        mensagem:
          "Por favor, adicione ao menos um objetivo específico (ou selecione 'Usar objetivo geral') e ao menos uma palavra-chave para continuar.",
      };
    }

    if (!temObjetivos) {
      return {
        valido: false,
        mensagem:
          "Por favor, selecione ao menos um objetivo específico ou marque 'Usar objetivo geral' para continuar.",
      };
    }

    if (!temPalavrasChave) {
      return {
        valido: false,
        mensagem:
          "Por favor, adicione ao menos uma palavra-chave para continuar.",
      };
    }

    return { valido: true };
  };

  /**
   * Valida o período de realização da atividade quando esta não possui encontros cadastrados.
   */
  const validarPeriodoRealizacao = (
    dataInicioRealizacao,
    dataFimRealizacao,
    possuiEncontros = true,
    dataFimInscricao = null
  ) => {
    if (possuiEncontros) {
      return { valido: true };
    }

    if (!dataInicioRealizacao || !dataFimRealizacao) {
      return {
        valido: false,
        mensagem:
          "Por favor, informe o período (data de início e fim) de realização da atividade.",
      };
    }

    const inicio = dayjs(dataInicioRealizacao);
    const fim = dayjs(dataFimRealizacao);

    if (fim.isBefore(inicio)) {
      return {
        valido: false,
        mensagem:
          "A data de término da atividade não pode ser anterior à data de início.",
      };
    }

    if (dataFimInscricao) {
      const fimInscricao = dayjs(dataFimInscricao);
      if (inicio.isBefore(fimInscricao)) {
        return {
          valido: false,
          mensagem: `A data de início da atividade não pode ser anterior à data de fim das inscrições (${formatarDataHora(
            dataFimInscricao
          )}).`,
        };
      }
    }

    return { valido: true };
  };

  /**
   * Valida o período de inscrições quando a atividade necessita de inscrição.
   */
  const validarPeriodoInscricao = (
    modalidadeInscricao,
    dataInicioInscricao,
    dataFimInscricao
  ) => {
    if (modalidadeInscricao !== "NESCESSITA_INSCRICAO") {
      return { valido: true };
    }

    if (!dataInicioInscricao || !dataFimInscricao) {
      return {
        valido: false,
        mensagem:
          "Por favor, informe o período completo de inscrições da atividade.",
      };
    }

    const inicio = dayjs(dataInicioInscricao);
    const fim = dayjs(dataFimInscricao);

    if (fim.isBefore(inicio)) {
      return {
        valido: false,
        mensagem:
          "A data de término das inscrições não pode ser anterior à data de início.",
      };
    }

    return { valido: true };
  };

  /**
   * Valida se a descrição da imagem para acessibilidade foi preenchida quando há imagem em anexo.
   */
  const validarDescricaoImagem = (imagem) => {
    if (imagem?.base64Data && !imagem?.descricaoImagem?.trim()) {
      return {
        valido: false,
        mensagem:
          "Por favor, informe a descrição da imagem para acessibilidade.",
      };
    }
    return { valido: true };
  };

  /**
   * Checadores individuais de validade de campos (usados em classes css ou computeds)
   */
  const verificarDataInicioInscricaoValida = (
    modalidadeInscricao,
    dataInicioInscricao
  ) => {
    if (modalidadeInscricao !== "NESCESSITA_INSCRICAO") return true;
    return Boolean(dataInicioInscricao);
  };

  const verificarDataFimInscricaoValida = (
    modalidadeInscricao,
    dataInicioInscricao,
    dataFimInscricao
  ) => {
    if (modalidadeInscricao !== "NESCESSITA_INSCRICAO") return true;
    if (!dataFimInscricao) return false;
    if (!dataInicioInscricao) return true;
    return (
      new Date(dataFimInscricao).getTime() >=
      new Date(dataInicioInscricao).getTime()
    );
  };

  const verificarDataInicioRealizacaoValida = (
    possuiEncontros,
    dataInicioRealizacao
  ) => {
    if (possuiEncontros) return true;
    return Boolean(dataInicioRealizacao);
  };

  const verificarDataFimRealizacaoValida = (
    possuiEncontros,
    dataInicioRealizacao,
    dataFimRealizacao
  ) => {
    if (possuiEncontros) return true;
    if (!dataFimRealizacao) return false;
    if (!dataInicioRealizacao) return true;
    return (
      new Date(dataFimRealizacao).getTime() >=
      new Date(dataInicioRealizacao).getTime()
    );
  };

  /**
   * Calcula a data mínima permitida para início da realização.
   */
  const calcularDataMinimaRealizacao = (
    dataFimInscricao,
    dataMinimaPadrao
  ) => {
    if (dataFimInscricao) {
      return dataFimInscricao;
    }
    return dataMinimaPadrao;
  };

  /**
   * Executa a bateria completa de validações de negócio da atividade.
   */
  const validarAtividadeCompleta = (formData, usarObjetivoGeral = false) => {
    if (!formData) return { valido: false, mensagem: "Dados inválidos." };

    const validacaoObj = validarObjetivosEPalavrasChave(
      formData.objetivos,
      usarObjetivoGeral,
      formData.palavrasChave
    );
    if (!validacaoObj.valido) return validacaoObj;

    const validacaoInscricao = validarPeriodoInscricao(
      formData.modalidadeInscricao,
      formData.dataInicioInscricao,
      formData.dataFimInscricao
    );
    if (!validacaoInscricao.valido) return validacaoInscricao;

    const validacaoRealizacao = validarPeriodoRealizacao(
      formData.dataInicioRealizacao,
      formData.dataFimRealizacao,
      formData.possuiEncontros,
      formData.dataFimInscricao
    );
    if (!validacaoRealizacao.valido) return validacaoRealizacao;

    const validacaoImg = validarDescricaoImagem(formData.imagem);
    if (!validacaoImg.valido) return validacaoImg;

    return { valido: true };
  };

  return {
    criarAtividadePadrao,
    validarObjetivosEPalavrasChave,
    validarPeriodoRealizacao,
    validarPeriodoInscricao,
    validarDescricaoImagem,
    verificarDataInicioInscricaoValida,
    verificarDataFimInscricaoValida,
    verificarDataInicioRealizacaoValida,
    verificarDataFimRealizacaoValida,
    calcularDataMinimaRealizacao,
    validarAtividadeCompleta,
  };
}
